import React, { useCallback, useState } from 'react';

import Dropzone from 'react-dropzone';
import { Box, FormHelperText, IconButton, Stack, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import { CustomIconButton } from './button';

export const FileInput = ({
                              validate,
                              onAttach,
                              label,
                              isAttached,
                              text,
                              errorMessage,
                              onRemove,
                              uploadedFileName
                          }) => {
    const [hasError, setHasError] = useState(false);
    const [filename, setFilename] = useState(uploadedFileName);

    const onDrop = useCallback((acceptedFiles) => {
        setHasError(false);
        const file = acceptedFiles[0];

        if (!file || !validate(file)) {
            setHasError(true);
            return;
        }

        setFilename(file.name);
        onAttach && onAttach(file);
    }, [onAttach, validate]);

    return (
        <Box sx={ { width: '100%' } }>
            { label && (
                <Typography variant="subtitle2" color="textSecondary"
                            sx={ { mb: 1.5, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' } }>
                    { label }
                </Typography>
            ) }

            { !isAttached ? (
                <Box>
                    <Dropzone maxFiles={ 1 } multiple={ false } onDrop={ onDrop }>
                        { ({ getRootProps, getInputProps }) => (
                            <Box
                                { ...getRootProps() }
                                sx={ {
                                    border: theme => `2px dashed ${ theme.palette.slate[300] }`,
                                    borderRadius: 2,
                                    backgroundColor: 'slate.50',
                                    py: 3,
                                    px: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        backgroundColor: 'slate.100'
                                    }
                                } }
                            >
                                <input { ...getInputProps() } />
                                <CloudUploadOutlinedIcon sx={ { fontSize: 32, color: 'primary.main', mb: 1 } }/>
                                <Typography variant="body2" color="textSecondary" textAlign="center" fontWeight={ 500 }>
                                    { text }
                                </Typography>
                            </Box>
                        ) }
                    </Dropzone>
                    { hasError && (
                        <FormHelperText error sx={ { mt: 1, px: 1 } }>
                            { errorMessage }
                        </FormHelperText>
                    ) }
                </Box>
            ) : (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={ {
                        border: theme => `1px solid ${ theme.palette.divider }`,
                        borderRadius: 2,
                        p: 1.5,
                        backgroundColor: 'background.paper'
                    } }
                >
                    <Stack direction="row" alignItems="center" spacing={ 1.5 }>
                        <DescriptionOutlinedIcon color="primary"/>
                        <Typography variant="body2" sx={ { fontWeight: 500, wordBreak: 'break-all' } }>
                            { filename || 'Załączono plik' }
                        </Typography>
                    </Stack>

                    { onRemove && (
                        <IconButton onClick={ onRemove } title="Usuń plik" size="small">
                            <DeleteOutlineIcon fontSize="small"/>
                        </IconButton>
                    ) }
                </Stack>
            ) }
        </Box>
    );
};

export const FilesInput = ({
                               disabled,
                               onAttach,
                               text,
                               label,
                               maxFiles,
                               acceptedFileExtensions,
                               maxTotalFilesSizeInMB,
                               uploadedFiles
                           }) => {
    const [error, setError] = useState(false);

    const onDrop = useCallback((acceptedFiles) => {
        setError(false);

        if (acceptedFiles?.length === 0) {
            setError('Niepoprawny plik.');
            return;
        }

        if (!checkFilesType(acceptedFiles, acceptedFileExtensions)) {
            setError('Niepoprawny format pliku.');
            return;
        }

        if (maxTotalFilesSizeInMB && uploadedFiles && !checkTotalFilesSize([...acceptedFiles, ...uploadedFiles], maxTotalFilesSizeInMB)) {
            setError('Przekroczony dozwolony łączny rozmiar plików.');
            return;
        }

        onAttach && onAttach(acceptedFiles);
    }, [acceptedFileExtensions, uploadedFiles, maxTotalFilesSizeInMB, onAttach]);

    if (maxFiles > 1 && uploadedFiles?.length >= maxFiles) {
        return (
            <Typography variant="body2" color="textSecondary">
                Maksymalna liczba plików została osiągnięta.
            </Typography>
        );
    }

    return (
        <Box sx={ { width: '100%' } }>
            { label && (
                <Typography variant="subtitle2" color="textSecondary"
                            sx={ { mb: 1.5, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' } }>
                    { label }
                </Typography>
            ) }

            <Box>
                <Dropzone disabled={ disabled } multiple={ false } onDrop={ onDrop }>
                    { ({ getRootProps, getInputProps }) => (
                        <Box
                            { ...getRootProps() }
                            sx={ {
                                border: theme => `2px dashed ${ theme.palette.slate[300] }`,
                                borderRadius: 2,
                                backgroundColor: disabled ? 'slate.100' : 'slate.50',
                                py: 3,
                                px: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: disabled ? 'default' : 'pointer',
                                transition: 'all 0.2s ease',
                                opacity: disabled ? 0.6 : 1,
                                '&:hover': {
                                    borderColor: disabled ? 'slate.300' : 'primary.main',
                                    backgroundColor: 'slate.100'
                                }
                            } }
                        >
                            <input { ...getInputProps() } />
                            <CloudUploadOutlinedIcon
                                sx={ { fontSize: 32, color: disabled ? 'text.disabled' : 'primary.main', mb: 1 } }/>
                            <Typography variant="body2" color="textSecondary" textAlign="center" fontWeight={ 500 }>
                                { text }
                            </Typography>
                        </Box>
                    ) }
                </Dropzone>
                { error && (
                    <FormHelperText error sx={ { mt: 1, px: 1 } }>
                        { error }
                    </FormHelperText>
                ) }
            </Box>
        </Box>
    );
};

export const UploadedFiles = ({
                                  label,
                                  uploadedFiles,
                                  onRemove,
                                  downloadOption,
                                  ondDownloadClick,
                                  isLoading,
                                  removeOption
                              }) => {
    if (!uploadedFiles?.length) return null;

    return (
        <Box sx={ { width: '100%' } }>
            { label && (
                <Typography variant="subtitle2" color="textSecondary"
                            sx={ { mb: 1.5, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' } }>
                    { label }
                </Typography>
            ) }
            <Stack spacing={ 1.5 }>
                { uploadedFiles.map((uploadedFile, index) => (
                    <Stack
                        key={ index }
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={ {
                            border: theme => `1px solid ${ theme.palette.divider }`,
                            borderRadius: 2,
                            p: 1.5,
                            backgroundColor: 'background.paper'
                        } }
                    >
                        <Stack direction="row" alignItems="center" spacing={ 1.5 } sx={ { overflow: 'hidden' } }>
                            <DescriptionOutlinedIcon color="primary" sx={ { flexShrink: 0 } }/>
                            <Typography variant="body2" sx={ {
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            } }>
                                { uploadedFile?.name || uploadedFile?.fileName || 'Załączony plik' }
                            </Typography>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={ 0.5 } sx={ { flexShrink: 0 } }>
                            { downloadOption && uploadedFile?.fileUrl && (
                                <CustomIconButton
                                    title="Pobierz plik"
                                    isLoading={ isLoading }
                                    onClick={ () => ondDownloadClick(uploadedFile.fileUrl) }
                                    icon={ <DownloadIcon fontSize="small"/> }
                                    size="small"
                                />
                            ) }
                            { removeOption && (
                                <IconButton onClick={ () => onRemove && onRemove(index) } title="Usuń plik" size="small">
                                    <DeleteOutlineIcon fontSize="small"/>
                                </IconButton>
                            ) }
                        </Stack>
                    </Stack>
                )) }
            </Stack>
        </Box>
    );
};

const checkTotalFilesSize = (files, maxSizeInMB) => {
    const totalSize = files.reduce((sum, file) => sum + (file.size || file.sizeInBytes), 0);
    return totalSize <= maxSizeInMB * 1024 * 1024;
};

const getFileExtension = (file) => {
    if (!file.name.match(/\./)) {
        return '';
    }
    return file.name.split('.').pop().toLowerCase();
};

const checkFilesType = (files, acceptedFileExtensions) => {
    for (const file of files) {
        if (!acceptedFileExtensions.includes(getFileExtension(file))) {
            return false;
        }
    }
    return true;
};

export default FileInput;